'use client';

import { fetchMarketingFlashSaleProductList } from '@/api/marketingPages';
import type { IProductListItem } from '@/api/types/product';
import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import type { PromotionCustomerFlashSale, PromotionFlashSaleCustomerSegment } from '../../../types';
import type { Currency } from '@/api/types/public';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { ToRightArrow } from '@zeelool/icon';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useValidExposure } from '@/hooks/useValidExposure';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  createFlashSaleProductCardClickHandler,
  getPcFlashSaleSwiperSlideStyle,
  PROMOTION_FLASH_SALE_PC_SPACE_BETWEEN,
} from './PromotionFlashSaleSection.utils';

const PC_SWIPER_SLIDE_STYLE = getPcFlashSaleSwiperSlideStyle();

interface PromotionFlashSaleSectionProps {
  pageId: number;
  config: PromotionCustomerFlashSale;
  newCustomerConfig?: PromotionCustomerFlashSale;
  oldCustomerConfig?: PromotionCustomerFlashSale;
  newUserRefId?: number;
  oldUserRefId?: number;
  products: IProductListItem[];
  customerSegment?: PromotionFlashSaleCustomerSegment;
  countdownEndAt?: string | null;
  remainingSeconds?: number | null;
  soldOutSort?: string | number;
  soldOutDisplay?: string | number;
  deviceType: number;
  adp?: string;
  refreshOnMount?: boolean;
  onProductClickTrack: (item: IProductListItem, index: number) => void;
  onProductExposureTrack: (item: IProductListItem, index: number) => void;
}

interface FlashSaleProductCardProps {
  item: FlashSaleProductItem;
  index: number;
  isWap: boolean;
  isCompactWap?: boolean;
  isSoldOut: boolean;
  buttonText: string;
  buttonColor?: string;
  buttonImage?: string;
  currency: Currency;
  userInfo: unknown;
  onProductClick: (item: IProductListItem, index: number) => void;
  onProductExposure: (item: IProductListItem, index: number) => void;
}

type FlashSaleProductItem = IProductListItem & {
  price?: string;
};

interface CachedFlashSaleState {
  displayConfig: PromotionCustomerFlashSale;
  products: IProductListItem[];
  countdownEndAt?: string | null;
  remainingSeconds?: number | null;
  cachedAt: number;
}

const ExposureDetector = memo(function ExposureDetector({
  item,
  index,
  onExposure,
}: {
  item: IProductListItem;
  index: number;
  onExposure: (item: IProductListItem, index: number) => void;
}) {
  const { ref } = useValidExposure({
    callback: () => item.id && onExposure(item, index),
    ts: item.id,
  });

  return <div ref={ref} className="pointer-events-none absolute inset-0" />;
});

const FALLBACK_CURRENCY = {
  name: 'USD',
  full_symbol: '$',
  symbol: '$',
  exchange_rate: '1.000000',
  symbol_prefix: '$',
  symbol_suffix: '',
} as Currency;

const FLASH_SALE_CACHE_PREFIX = 'promotion-flash-sale';

function getFlashSaleCacheKey({
  pageId,
  newUserRefId,
  oldUserRefId,
  token,
  adp,
}: {
  pageId: number;
  newUserRefId?: number;
  oldUserRefId?: number;
  token: string;
  adp?: string;
}): string | null {
  if (!pageId || !newUserRefId || !oldUserRefId || !token) {
    return null;
  }

  return `${FLASH_SALE_CACHE_PREFIX}:${pageId}:${newUserRefId}:${oldUserRefId}:${token}:${adp ?? ''}`;
}

function readCachedFlashSaleState(cacheKey: string | null): CachedFlashSaleState | null {
  if (!cacheKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }

    const cached = JSON.parse(raw) as CachedFlashSaleState;
    if (!Array.isArray(cached.products) || !cached.displayConfig) {
      return null;
    }

    if (typeof cached.remainingSeconds === 'number' && cached.remainingSeconds > 0) {
      const elapsedSeconds = Math.floor((Date.now() - cached.cachedAt) / 1000);
      return {
        ...cached,
        remainingSeconds: Math.max(cached.remainingSeconds - elapsedSeconds, 0),
      };
    }

    return cached;
  } catch {
    window.sessionStorage.removeItem(cacheKey);
    return null;
  }
}

function writeCachedFlashSaleState(cacheKey: string | null, state: CachedFlashSaleState) {
  if (!cacheKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(state));
  } catch {
    // Ignore storage quota or private mode failures; the live response still updates the UI.
  }
}

function isSoldOutProduct(item: IProductListItem): boolean {
  const status = Number(item.status);
  const stock = Number(item.stock);

  return status !== 1 || (!Number.isNaN(stock) && stock <= 0);
}

function getProductImage(item: IProductListItem): string {
  const image = item.image;

  if (Array.isArray(image?.product) && image.product[0]) {
    return image.product[0];
  }

  if (typeof image === 'string') {
    return image;
  }

  return '';
}

function getProductName(item: IProductListItem): string {
  return item.short_name || item.name || item.shop_sku || '';
}

function getDisplayPrice(item: FlashSaleProductItem, currency: Currency, userInfo: unknown) {
  const priceInfo = getPriceInfo(userInfo, item, currency) as {
    price?: string;
    sell_price?: string;
  };

  return {
    price: priceInfo.price || item.price || '',
    sellPrice: priceInfo.sell_price || priceInfo.price || item.price || '',
  };
}

function chunkProducts(list: IProductListItem[], chunkSize: number): IProductListItem[][] {
  const chunks: IProductListItem[][] = [];

  list.forEach((item, index) => {
    if (index % chunkSize === 0) {
      chunks.push([]);
    }
    chunks[chunks.length - 1].push(item);
  });

  return chunks;
}

function formatTimePart(value: number): string {
  return `${Math.max(value, 0)}`.padStart(2, '0');
}

function useFlashSaleCountdown(countdownEndAt?: string | null, remainingSeconds?: number | null) {
  const initialSeconds = useMemo(() => {
    if (typeof remainingSeconds === 'number' && remainingSeconds > 0) {
      return remainingSeconds;
    }

    if (countdownEndAt) {
      const diffSeconds = Math.floor((new Date(countdownEndAt).getTime() - Date.now()) / 1000);
      return Math.max(diffSeconds, 0);
    }

    return 0;
  }, [countdownEndAt, remainingSeconds]);

  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    setSecondsLeft(initialSeconds);

    if (initialSeconds <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [initialSeconds]);

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;

  return [hours, minutes, seconds] as const;
}

function FlashSaleProductCard({
  item,
  index,
  isWap,
  isCompactWap,
  isSoldOut,
  buttonText,
  buttonColor,
  buttonImage,
  currency,
  userInfo,
  onProductClick,
  onProductExposure,
}: FlashSaleProductCardProps) {
  const t = useTranslations('activity.flashSale');
  const productImage = getProductImage(item);
  const productName = getProductName(item);
  const { price, sellPrice } = getDisplayPrice(item, currency, userInfo);

  return (
    <a
      href={item.shop_sku ? `/goods-detail/${item.shop_sku}` : undefined}
      className={`relative box-border block w-full overflow-hidden bg-white text-left transition-opacity ${
        isWap
          ? `rounded-[8px] flex ${isCompactWap ? 'h-[94px]' : 'h-[121px]'} items-center px-[20px]`
          : 'rounded-[12px] min-h-[280px] pb-[32px] pt-[70px]'
      }`}
      onClick={createFlashSaleProductCardClickHandler({ item, index, onProductClick })}
      aria-label={productName}
    >
      <div
        className={`${isWap ? 'relative h-[60px] w-[120px] mr-[46px]' : 'relative mx-auto h-[138px] w-[260px]'}`}
      >
        {productImage && (
          <Image
            src={productImage}
            alt={productName}
            fill
            sizes={isWap ? '45vw' : '25vw'}
            className="object-cover"
          />
        )}
        {isSoldOut && (
          <span
            className={`pointer-events-none absolute left-1/2 top-1/2 z-[2] flex aspect-square -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center ${isWap ? 'h-[66px] w-[66px]' : 'h-[5.729vw] max-h-[110px] w-[5.729vw] max-w-[110px]'}`}
          >
            <div
              className={`whitespace-pre-wrap text-white ${
                isWap
                  ? 'text-[11px] leading-[13px]'
                  : 'text-[clamp(12px,0.885vw,17px)] leading-[clamp(14px,1.042vw,20px)]'
              }`}
              dangerouslySetInnerHTML={{ __html: t('soldOut') }}
            />
          </span>
        )}
      </div>

      <div
        className={`${
          isWap
            ? 'min-w-0'
            : 'mt-[30px] flex min-h-[92px] flex-col items-center justify-end text-center'
        }`}
      >
        <div className={`truncate font-bs text-primary-black  ${isWap ? 'text-12' : 'text-16'}`}>
          {productName}
        </div>
        {price && price !== sellPrice && (
          <div
            className={`text-auxiliary-dark-gray line-through ${isWap ? 'mt-[4px] text-12' : 'mt-[8px] text-16'}`}
          >
            {formatPriceNumberWithSymbol(price, currency)}
          </div>
        )}
        <div
          className={` flex overflow-hidden rounded-[999px] font-bb text-white ${
            isWap ? 'min-w-[132px] mt-[8px]  h-[28px]' : 'mt-[16px] h-[56px] min-w-[268px]'
          }`}
        >
          <span
            className={`flex flex-1 items-center whitespace-nowrap ${isWap ? 'text-[13px] pl-[12px]' : 'text-[26px] pl-[28px]'}`}
            style={{ backgroundColor: buttonColor || '#463AE8' }}
          >
            {formatPriceNumberWithSymbol(sellPrice, currency)}
          </span>
          <span
            className={`relative -ml-[1px] flex items-center ${isWap ? 'text-[13px] min-w-[57px] pl-[22px]' : 'text-[26px] min-w-[120px] pl-[46px]'}`}
            style={{
              backgroundColor: buttonImage ? undefined : '#222222',
              backgroundImage: buttonImage ? `url(${buttonImage})` : undefined,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
            }}
          >
            {!buttonImage && (
              <span className="absolute left-[-12px] top-0 h-full w-[35px] bg-[#fff7c6] [clip-path:polygon(55%_0,100%_0,62%_45%,100%_45%,38%_100%,48%_57%,0_57%)]" />
            )}
            <span className="relative z-[1]">{buttonText}</span>
          </span>
        </div>
      </div>

      <ExposureDetector item={item} index={index} onExposure={onProductExposure} />

      {isSoldOut && (
        <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-white/40 lg:rounded-[10px] 2xl:rounded-[12px]" />
      )}
    </a>
  );
}

export default function PromotionFlashSaleSection({
  pageId,
  config,
  newCustomerConfig,
  oldCustomerConfig,
  newUserRefId,
  oldUserRefId,
  products,
  countdownEndAt,
  remainingSeconds,
  soldOutSort,
  soldOutDisplay,
  deviceType,
  adp,
  refreshOnMount = false,
  onProductClickTrack,
  onProductExposureTrack,
}: PromotionFlashSaleSectionProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const t = useTranslations('activity.flashSale');
  const { navigate } = useAppNavigation();
  const currency = useStore((state) => state.currency) || FALLBACK_CURRENCY;
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const swiperRef = useRef<SwiperType | null>(null);
  const previousTokenRef = useRef<string | undefined>(undefined);
  const latestProductRequestIdRef = useRef(0);
  const cacheKey = getFlashSaleCacheKey({
    pageId,
    newUserRefId,
    oldUserRefId,
    token,
    adp,
  });
  const cachedFlashSaleState = useMemo(() => readCachedFlashSaleState(cacheKey), [cacheKey]);
  const [displayConfig, setDisplayConfig] = useState(
    () => cachedFlashSaleState?.displayConfig || config,
  );
  const [currentProducts, setCurrentProducts] = useState<IProductListItem[]>(
    () => cachedFlashSaleState?.products || products,
  );
  const [currentCountdownEndAt, setCurrentCountdownEndAt] = useState(
    () => cachedFlashSaleState?.countdownEndAt ?? countdownEndAt,
  );
  const [currentRemainingSeconds, setCurrentRemainingSeconds] = useState(
    () => cachedFlashSaleState?.remainingSeconds ?? remainingSeconds,
  );
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [hours, minutes, seconds] = useFlashSaleCountdown(
    currentCountdownEndAt,
    currentRemainingSeconds,
  );
  const shouldHideSoldOut = Number(soldOutDisplay) === 2;
  const shouldSortSoldOutLast = Number(soldOutSort) === 2;
  const bgImage = isWap
    ? displayConfig.bg_wap_image || displayConfig.bg_pc_image
    : displayConfig.bg_pc_image || displayConfig.bg_wap_image;
  const buttonImage = isWap
    ? displayConfig.button_wap_image || displayConfig.button_pc_image
    : displayConfig.button_pc_image || displayConfig.button_wap_image;

  const productList = useMemo<FlashSaleProductItem[]>(() => {
    const normalized = (currentProducts as FlashSaleProductItem[]).filter(
      (item) => !shouldHideSoldOut || !isSoldOutProduct(item),
    );

    if (!shouldSortSoldOutLast) {
      return normalized;
    }

    return [...normalized].sort(
      (a, b) => Number(isSoldOutProduct(a)) - Number(isSoldOutProduct(b)),
    );
  }, [currentProducts, shouldHideSoldOut, shouldSortSoldOutLast]);

  const refreshProductsForIdentityChange = useCallback(async () => {
    if (!newUserRefId || !oldUserRefId) return;

    const requestId = latestProductRequestIdRef.current + 1;
    latestProductRequestIdRef.current = requestId;
    const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

    try {
      const response = await fetchMarketingFlashSaleProductList({
        newUserRefId,
        oldUserRefId,
        page: 1,
        pageSize: 8,
        pageId,
        adp,
      });
      if (!isLatestRequest()) return;

      const nextSegment = response?.customer_segment === 'old_user' ? 'old_user' : 'new_user';
      const nextConfig =
        nextSegment === 'old_user' ? oldCustomerConfig || config : newCustomerConfig || config;

      setDisplayConfig(nextConfig);
      const nextProducts = (response?.items || []) as IProductListItem[];
      setCurrentProducts(nextProducts);
      setCurrentCountdownEndAt(response?.countdown_end_at ?? null);
      setCurrentRemainingSeconds(response?.remaining_seconds ?? null);
      writeCachedFlashSaleState(cacheKey, {
        displayConfig: nextConfig,
        products: nextProducts,
        countdownEndAt: response?.countdown_end_at ?? null,
        remainingSeconds: response?.remaining_seconds ?? null,
        cachedAt: Date.now(),
      });
    } catch (error) {
      if (isLatestRequest()) {
        logger.error(error, 'Failed to refresh promotion flash sale products:');
      }
    }
  }, [
    adp,
    cacheKey,
    config,
    newCustomerConfig,
    newUserRefId,
    oldCustomerConfig,
    oldUserRefId,
    pageId,
  ]);

  useEffect(() => {
    const previousToken = previousTokenRef.current;
    const isFirstRender = previousToken === undefined;
    const hasTokenChanged = !isFirstRender && previousToken !== token;

    previousTokenRef.current = token;

    if (isFirstRender && !token && !refreshOnMount) return;
    if (!isFirstRender && !hasTokenChanged) return;

    refreshProductsForIdentityChange();
  }, [refreshOnMount, refreshProductsForIdentityChange, token]);

  const handleProductClick = useCallback(
    (item: IProductListItem, index: number) => {
      onProductClickTrack(item, index);

      if (item.shop_sku) {
        navigate(`/goods-detail/${item.shop_sku}`);
      }
    },
    [navigate, onProductClickTrack],
  );

  const syncSwiperEdgeState = useCallback((swiper: SwiperType) => {
    setIsBeginning((current) => (current === swiper.isBeginning ? current : swiper.isBeginning));
    setIsEnd((current) => (current === swiper.isEnd ? current : swiper.isEnd));
  }, []);

  if (productList.length === 0) {
    return null;
  }

  const countdownParts = [hours, minutes, seconds];
  const shouldShowCountdown = countdownParts.some((part) => part > 0);
  const isCompactWap = isWap && productList.length === 4;
  const wapShouldSwipe = productList.length > 4;
  const pcShouldSwipe = productList.length > 3;

  const renderProductCard = (item: FlashSaleProductItem, index: number) => (
    <FlashSaleProductCard
      key={`${item.id || item.shop_sku}-${index}`}
      item={item}
      index={index}
      isWap={isWap}
      isCompactWap={isCompactWap}
      isSoldOut={isSoldOutProduct(item)}
      buttonText={displayConfig.button_text || t('buy')}
      buttonColor={displayConfig.button_color}
      buttonImage={buttonImage}
      currency={currency}
      userInfo={userInfo}
      onProductClick={handleProductClick}
      onProductExposure={onProductExposureTrack}
    />
  );

  return (
    <section
      className={isWap ? 'bg-[#f4f4f4] px-[8px] pt-[14px]' : 'bg-[#f4f4f4] px-[48px] pt-[36px]'}
    >
      <div
        className={`overflow-hidden rounded-[10px] bg-cover bg-center ${
          isWap ? 'px-[10px] pb-[12px] pt-[12px]' : 'px-[40px] pb-[23px] pt-[32px]'
        }`}
        style={{
          backgroundColor: displayConfig.background_color || '#b7b2ff',
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% auto',
        }}
      >
        <div
          className={`flex ${
            isWap
              ? 'mb-[12px] gap-[3px] flex-wrap items-center justify-between'
              : 'mb-[29px] gap-[8px] items-center justify-between'
          }`}
        >
          <div className={isWap ? 'min-w-0 flex-1' : 'flex min-w-0 items-baseline gap-[16px]'}>
            <h2
              className={`truncate font-bb text-primary-black ${
                isWap ? 'text-20 leading-[24px]' : 'text-[40px] leading-[48px]'
              }`}
            >
              {displayConfig.title}
            </h2>
            {displayConfig.sub_title && !isWap && (
              <p className="truncate text-16 leading-[19px] text-primary-black">
                {displayConfig.sub_title}
              </p>
            )}
          </div>

          {shouldShowCountdown && (
            <div
              className={`flex flex-none items-center font-bb text-primary-black ${isWap ? 'gap-[8px]' : 'gap-[16px]'}`}
            >
              <span className={isWap ? 'text-[12px]' : 'text-[16px]'}>{t('endsIn')}</span>
              <div className="flex items-center gap-[3px]">
                {countdownParts.map((part, index) => (
                  <span
                    key={index}
                    className={`flex items-center ${isWap ? 'gap-[3px]' : 'gap-[5px]'}`}
                  >
                    <span
                      className={`bg-primary-black  font-bb text-white flex items-center justify-center ${isWap ? 'rounded-[4px] h-[20px] w-[20px] text-[11px]' : 'rounded-[9px] h-[40px] w-[40px] text-[22px]'}`}
                    >
                      {formatTimePart(part)}
                    </span>
                    {index < countdownParts.length - 1 && (
                      <span
                        className={`font-bb text-primary-black ${isWap ? 'text-[11px]' : 'text-[22px]'}`}
                      >
                        :
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          {displayConfig.sub_title && isWap && (
            <p className="w-full truncate text-12 leading-[14px] text-primary-black">
              {displayConfig.sub_title}
            </p>
          )}
        </div>

        {isWap ? (
          wapShouldSwipe ? (
            <Swiper
              modules={[Pagination]}
              slidesPerView={1}
              slidesPerGroup={1}
              spaceBetween={12}
              speed={420}
              threshold={8}
              resistanceRatio={0.65}
              roundLengths
              pagination={{ clickable: true }}
              className="promotion-flash-sale-wap-swiper !overflow-hidden !pb-[22px] [&_.swiper-pagination]:!bottom-[2px] [&_.swiper-pagination-bullet]:!h-[6px] [&_.swiper-pagination-bullet]:!w-[6px] [&_.swiper-pagination-bullet]:!bg-white [&_.swiper-pagination-bullet]:!opacity-70 [&_.swiper-pagination-bullet-active]:!w-[18px] [&_.swiper-pagination-bullet-active]:!rounded-[999px] [&_.swiper-pagination-bullet-active]:!opacity-100 [&_.swiper-wrapper]:!ease-out"
            >
              {chunkProducts(productList, 3).map((group, groupIndex) => (
                <SwiperSlide key={groupIndex} className="!h-auto !w-full">
                  <div className="box-border flex w-full flex-col gap-[4px]">
                    {group.map((item, itemIndex) =>
                      renderProductCard(item, groupIndex * 3 + itemIndex),
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="flex flex-col gap-[4px]">
              {productList.map((item, index) => renderProductCard(item, index))}
            </div>
          )
        ) : pcShouldSwipe ? (
          <div className="group relative">
            <Swiper
              modules={[Navigation]}
              slidesPerView="auto"
              slidesPerGroup={1}
              spaceBetween={PROMOTION_FLASH_SALE_PC_SPACE_BETWEEN}
              navigation={{
                nextEl: '.promotion-flash-sale-next',
                prevEl: '.promotion-flash-sale-prev',
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                syncSwiperEdgeState(swiper);
              }}
              onSlideChange={syncSwiperEdgeState}
              onProgress={syncSwiperEdgeState}
              onReachBeginning={syncSwiperEdgeState}
              onReachEnd={syncSwiperEdgeState}
              onFromEdge={syncSwiperEdgeState}
              onTransitionEnd={syncSwiperEdgeState}
              className="!overflow-visible"
            >
              {productList.map((item, index) => (
                <SwiperSlide
                  key={`${item.id || item.shop_sku}-${index}`}
                  style={PC_SWIPER_SLIDE_STYLE}
                >
                  {renderProductCard(item, index)}
                </SwiperSlide>
              ))}
            </Swiper>

            <button
              type="button"
              className={`cursor-pointer promotion-flash-sale-prev absolute left-[12px] top-1/2 z-[2] flex h-[70px] w-[100px] -translate-y-1/2 items-center justify-center rounded-[999px] bg-primary-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 ${
                isBeginning ? 'hidden' : ''
              }`}
              aria-label="Previous"
            >
              <ToRightArrow className="relative right-[1px] text-[40px] rotate-180 text-white" />
            </button>
            <button
              type="button"
              className={`cursor-pointer promotion-flash-sale-next absolute right-[12px] top-1/2 z-[2] flex h-[70px] w-[100px] -translate-y-1/2 items-center justify-center rounded-[999px] bg-primary-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 ${
                isEnd ? 'hidden' : ''
              }`}
              aria-label="Next"
            >
              <ToRightArrow className="text-[40px] text-white" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-[12px]">
            {productList.map((item, index) => renderProductCard(item, index))}
          </div>
        )}
      </div>
    </section>
  );
}
